import React, { useEffect, useState } from 'react';

import { Upload } from 'antd';
import {
  CreditCardOutlined,
  LoadingOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';

import { colors } from '../../../../../common/styles/style-constants';
import {
  Div,
  FlexCol,
  H3,
  P,
} from '../../../../../common/styles/oc-styled-components';
import { updateCheckoutFormData } from '../../../../../connect/actions';
import { selectDataCheckoutFormData } from '../../../../../connect/selectors';
import { readAsDataUrl } from '../../../../../utils/files';

import {
  InsuranceCardUploaderCol,
  InsuranceCardUploaderRow,
  SageLink,
} from './styled-components';

const InsuranceCardUploader = ({ margin }) => {
  const ACCEPT_FILE_TYPES = [
    'image/jpg',
    'image/jpeg',
    'image/png',
    'image/gif',
  ];

  const dispatch = useDispatch();

  // TODO: move to the upper scope for the better component isolation.
  const checkoutData = useSelector(selectDataCheckoutFormData);

  const [frontImageLoading, setFrontImageLoading] = useState(false);
  const [frontImageUrl, setFrontImageUrl] = useState(null);
  const [backImageLoading, setBackImageLoading] = useState(false);
  const [backImageUrl, setBackImageUrl] = useState(null);

  useEffect(() => {
    if (checkoutData) {
      if (checkoutData.cardFrontDataUrl)
        setFrontImageUrl(checkoutData.cardFrontDataUrl);
      if (checkoutData.cardBackDataUrl)
        setBackImageUrl(checkoutData.cardBackDataUrl);
    }
  }, [checkoutData]);

  const beforeUpload = file => ACCEPT_FILE_TYPES.includes(file.type);

  const handleStateChange = (state, side) => {
    if (state.file.status === 'uploading') {
      if (side === 'front') {
        setFrontImageLoading(true);
      } else if (side === 'back') {
        setBackImageLoading(true);
      }
      return;
    }
    if (['done', 'error'].includes(state.file.status)) {
      readAsDataUrl(state.file.originFileObj, dataUrl => {
        if (side === 'front') {
          setFrontImageLoading(false);
          setFrontImageUrl(dataUrl);
          dispatch(
            updateCheckoutFormData({
              cardFrontDataUrl: dataUrl,
            }),
          );
        } else if (side === 'back') {
          setBackImageLoading(false);
          setBackImageUrl(dataUrl);
          dispatch(
            updateCheckoutFormData({
              cardBackDataUrl: dataUrl,
            }),
          );
        }
      });
    }
  };

  const resetPhoto = side => {
    if (side === 'front') {
      setFrontImageUrl(null);
      dispatch(
        updateCheckoutFormData({
          cardFrontDataUrl: undefined,
        }),
      );
    } else if (side === 'back') {
      setBackImageUrl(null);
      dispatch(
        updateCheckoutFormData({
          cardBackDataUrl: undefined,
        }),
      );
    }
  };

  return (
    <Div margin={margin}>
      <H3 margin="0 0 3px">Take a photo of your dental insurance card</H3>
      <P margin="0 0 15px">
        Help your dentist verify eligibility & provide cost estimates prior to
        your appointment. This is optional and will quicken the scheduling
        process!
      </P>

      <InsuranceCardUploaderRow>
        <InsuranceCardUploaderCol>
          <Upload
            accept={ACCEPT_FILE_TYPES.join(',')}
            listType="picture-card"
            showUploadList={false}
            disabled={frontImageLoading}
            maxCount={1}
            beforeUpload={beforeUpload}
            onChange={state => handleStateChange(state, 'front')}
          >
            {frontImageUrl
              ? (
                <img
                  src={frontImageUrl}
                  className="insurance-card-img"
                  alt="Front side"
                />
              )
              : (
                <FlexCol>
                  <Div
                    margin="0 0 5px"
                    color={colors.sage7}
                    style={{ fontSize: '24px' }}
                  >
                    {frontImageLoading ? <LoadingOutlined /> : <IdcardOutlined />}
                  </Div>
                  <Div>
                    Capture the{' '}
                    <strong style={{ color: colors.sage7 }}>front</strong> of your
                    dental insurance card
                  </Div>
                </FlexCol>
              )
            }
          </Upload>
          {frontImageUrl 
            && (
              <Div margin="5px 0 0" textAlign="center">
                <SageLink onClick={() => resetPhoto('front')}>
                  Remove front photo
                </SageLink>
              </Div>
            )
          }
        </InsuranceCardUploaderCol>

        <InsuranceCardUploaderCol>
          <Upload
            accept={ACCEPT_FILE_TYPES.join(',')}
            listType="picture-card"
            showUploadList={false}
            disabled={backImageLoading}
            maxCount={1}
            beforeUpload={beforeUpload}
            onChange={state => handleStateChange(state, 'back')}
          >
            {backImageUrl
              ? (
                <img
                  src={backImageUrl}
                  className="insurance-card-img"
                  alt="Back side"
                />
              )
              : (
                <FlexCol>
                  <Div
                    margin="0 0 5px"
                    color={colors.sage7}
                    style={{ fontSize: '24px' }}
                  >
                    {backImageLoading
                      ? <LoadingOutlined />
                      : <CreditCardOutlined />
                    }
                  </Div>
                  <Div>
                    Capture the{' '}
                    <strong style={{ color: colors.sage7 }}>back</strong> of your
                    dental insurance card
                  </Div>
                </FlexCol>
              )
            }
          </Upload>
          {backImageUrl 
            && (
              <Div margin="5px 0 0" textAlign="center">
                <SageLink onClick={() => resetPhoto('back')}>
                  Remove back photo
                </SageLink>
              </Div>
            )
          }
        </InsuranceCardUploaderCol>
      </InsuranceCardUploaderRow>
    </Div>
  );
};

InsuranceCardUploader.propTypes = {
  margin: PropTypes.string,
};

export default InsuranceCardUploader;
